import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formatDate, formatCurrency } from '../utils/numberFormatter';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useNavigate } from 'react-router-dom';
import '../styles/bitacora.css';
import '../styles/sales.css';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { PdfViewerModal } from '../components/Common';
import { TableCard } from '../components/ui';

const MySwal = withReactContent(Swal);

const COLLECTION_STATUSES = {
  pending: { label: 'Pendiente', class: 'sale-status-pending' },
  processed: { label: 'Procesado', class: 'sale-status-processed' },
  rejected: { label: 'Rechazado', class: 'sale-status-rejected' },
};

const CollectionPage = () => {
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    from_date: '',
    to_date: '',
    amount: ''
  });
  const [searchInput, setSearchInput] = useState('');
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  // Estados para el modal del PDF
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfTitle, setPdfTitle] = useState('');

  useEffect(() => {
    fetchCollections();
  }, [filters, pagination.current_page]);

  useEffect(() => {
    const initialSearch = searchInput.length === 0 && !filters.search;
    if (!initialSearch && (searchInput.length === 0 || searchInput.length >= 3)) {
      setFilters(prev => ({
        ...prev,
        search: searchInput
      }));
      setPagination(prev => ({ ...prev, current_page: 1 }));
    }
  }, [searchInput]);

  useEffect(() => {
    const hasFilters = Object.values(filters).some(value => value !== '') || searchInput !== '';
    setHasActiveFilters(hasFilters);
  }, [filters, searchInput]);

  const fetchCollections = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        ...filters,
        page: pagination.current_page,
        per_page: pagination.per_page
      });

      const response = await api.get(`/collections?${params}`);
      setCollections(response.data.collections);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error al obtener cobros:', error);
      MySwal.fire({
        title: 'Error',
        text: 'No se pudieron cargar los cobros',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'search') {
      setSearchInput(value);
    } else {
      setFilters(prev => ({
        ...prev,
        [name]: value
      }));
      setPagination(prev => ({ ...prev, current_page: 1 }));
    }
  };

  const getStatusBadgeClass = (status) => {
    return COLLECTION_STATUSES[status]?.class || 'sale-status-default';
  };

  const formatStatus = (status) => {
    return COLLECTION_STATUSES[status]?.label || status;
  };

  const handleGoToCustomer = (dni) => {
    navigate(`/clientes/${dni}`);
  };

  const handleViewReceipt = (collection, type) => {
    if (type === 'system' && collection.files?.receipt_url) {
      setPdfUrl(collection.files.receipt_url);
      setPdfTitle('Comprobante de Pago');
      setShowPdfModal(true);
    } else if (type === 'user' && collection.files?.voucher_url) {
      setPdfUrl(collection.files.voucher_url);
      setPdfTitle('Comprobante de Transferencia');
      setShowPdfModal(true);
    } else {
      MySwal.fire({
        title: 'Error',
        text: 'No hay comprobante disponible para este pago',
        icon: 'error'
      });
    }
  };

  const handleDownloadReceipt = async (collection) => {
    try {
      const response = await api.get(`/customers/${collection.applicant.id}/payments/${collection.uid}/receipt`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `comprobante-${collection.uid}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

    } catch (error) {
      console.error('Error downloading receipt:', error);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al descargar el comprobante.',
      });
    }
  };

  const handleClearFilters = () => {
    if (!hasActiveFilters) return;
    
    setFilters({
      search: '',
      status: '',
      from_date: '',
      to_date: '',
      amount: ''
    });
    setSearchInput('');
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  // Componente Skeleton para la tabla
  const TableSkeleton = () => (
    <>
      {Array.from({ length: pagination.per_page }).map((_, index) => (
        <tr key={index}>
          <td><Skeleton width={40} height={30} /></td>
          <td><Skeleton width={80} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td className="text-center">
            <Skeleton width={50} height={25} />
          </td>
          <td>
            <div className="d-flex gap-2">
              <Skeleton width={30} height={30} />
              <Skeleton width={30} height={30} />
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  // Componente de Paginación
  const Pagination = ({ loading }) => (
    <nav aria-label="Navegación de páginas">
      <ul className="pagination justify-content-center mb-0">
        <li className={`page-item ${loading || pagination.current_page === 1 ? 'disabled' : ''}`}>
          <button 
            className="page-link" 
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
            disabled={loading}
          >
            Anterior
          </button>
        </li>
        {Array.from({ length: pagination.last_page }, (_, i) => (
          <li key={i} className={`page-item ${pagination.current_page === i + 1 ? 'active' : ''} ${loading ? 'disabled' : ''}`}>
            <button 
              className="page-link"
              onClick={() => setPagination(prev => ({ ...prev, current_page: i + 1 }))}
              disabled={loading}
            >
              {i + 1}
            </button>
          </li>
        ))}
        <li className={`page-item ${loading || pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
          <button 
            className="page-link"
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
            disabled={loading}
          >
            Siguiente
          </button>
        </li>
      </ul>
    </nav>
  );

  return (
    <div className="page-content">
      <div className="page-title">
        <div className="row">
          <div className="col-12 col-md-6 order-md-1 order-last">
            <h3>Cobros</h3>
          </div>
        </div>
      </div>

      <section className="section">
        <TableCard
          toolbar={
            <div className="row align-items-end">
              <div className="col-md-2 mb-2">
                <label className="form-label">Buscar</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nombre, DNI o Referencia..."
                  title="Buscar por Nombre, DNI o Referencia..."
                  name="search"
                  value={searchInput}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="col-md-2 mb-2">
                <label className="form-label">Estado</label>
                <select
                  className="form-select"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option value="">Todos</option>
                  {Object.entries(COLLECTION_STATUSES).map(([value, { label }]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2 mb-2">
                <label className="form-label">Monto</label>
                <input
                  type="number"
                  className="form-control"
                  name="amount"
                  value={filters.amount}
                  onChange={handleFilterChange}
                  placeholder="Ingrese monto"
                />
              </div>
              <div className="col-md-2 mb-2">
                <label className="form-label">Desde</label>
                <input
                  type="date"
                  className="form-control"
                  name="from_date"
                  value={filters.from_date}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="col-md-2 mb-2">
                <label className="form-label">Hasta</label>
                <input
                  type="date"
                  className="form-control"
                  name="to_date"
                  value={filters.to_date}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="col-md-2 mb-2">
                <label className="form-label">&nbsp;</label>
                <button 
                  className={`btn ${hasActiveFilters ? 'btn-secondary' : 'btn-outline-secondary'} w-100`}
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Limpiar
                </button>
              </div>
            </div>
          }
          table={
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Referencia</th>
                    <th>Cliente</th>
                    <th>Creado</th>
                    <th>Fecha Imputación</th>
                    <th>Monto</th>
                    <th>Medio de Pago</th>
                    <th>Detalle</th>
                    <th className="text-center">Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <TableSkeleton />
                  ) : collections.length > 0 ? (
                    collections.map((collection) => (
                      <tr key={collection.id}>
                        <td>{collection.id}</td>
                        <td>{collection.uid}</td>
                        <td>
                          <button
                            type="button"
                            onClick={(e) => {
                              handleGoToCustomer(collection.applicant.dni);
                            }}
                            className="rg-link-button"
                          >
                            {collection.applicant.fullname}
                          </button>
                          <br />
                          <small className="text-muted">
                            DNI: {collection.applicant.dni}
                          </small>
                        </td>
                        <td>{formatDate(collection.created_at)}</td>
                        <td>{formatDate(collection.date)}</td>
                        <td>{formatCurrency(collection.amount.value)}</td>
                        <td>{collection.method}</td>
                        <td>
                          {collection.notes && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-${collection.id}`} className="text-wrap">
                                  <div className="p-1">{collection.notes}</div>
                                </Tooltip>
                              }
                            >
                              <div className="d-inline-block text-truncate rg-ellipsis-note">
                                <span className="badge bg-light text-dark">
                                  {collection.notes}
                                </span>
                              </div>
                            </OverlayTrigger>
                          )}
                        </td>
                        <td className="text-center">
                          <span className={`sale-status-badge ${getStatusBadgeClass(collection.status)}`}>
                            {formatStatus(collection.status)}
                          </span>
                        </td>
                        <td>
                          <div className="btn-group">
                            <button 
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleViewReceipt(collection, 'system')}
                              title="Ver comprobante de pago"
                            >
                              <i className="bi bi-receipt me-1"></i>
                              Ver Comprobante
                            </button>
                            <button 
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleViewReceipt(collection, 'user')}
                              title="Ver comprobante de transferencia"
                              disabled={!collection.files?.voucher_url}
                            >
                              <i className="bi bi-paperclip me-1"></i>
                              Ver Voucher
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center py-4">
                        <div className="d-flex flex-column align-items-center">
                          <i className="bi bi-inbox text-muted rg-empty-icon-lg"></i>
                          <p className="mt-4 mb-0 text-muted">
                            {hasActiveFilters ? (
                              <>
                                No se encontraron resultados para los filtros seleccionados.
                                <br />
                                <small>Intenta con otros criterios de búsqueda.</small>
                              </>
                            ) : (
                              'No hay cobros registrados.'
                            )}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          }
          pagination={<Pagination loading={loading} />}
        />
      </section>

      {/* Modal para vista previa de PDF */}
      <PdfViewerModal
        show={showPdfModal}
        onHide={() => setShowPdfModal(false)}
        url={pdfUrl}
        title={pdfTitle}
      />
    </div>
  );
};

export default CollectionPage;
